import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BundleType, BundleTypeKeys, type RootState } from '@/store';
import { BundleSearchResponse } from '@/api';

interface BundleSearchState {
  bundleSearchResults: BundleSearchResponse;
  bundleSearchText: string | null;
  bundleSearchHighlightPageId: string | null;
  bundleSearchFocusedPosition: number | null;
}

const initialState: BundleSearchState = {
  bundleSearchResults: {
    pageIds: [],
    resultsPerPage: {}
  },
  bundleSearchText: null,
  bundleSearchHighlightPageId: null,
  bundleSearchFocusedPosition: null
};

const bundleSearchSlice = createSlice({
  name: 'bundleSearch',
  initialState: {
    [BundleType.LIGHT]: initialState,
    [BundleType.FULL]: initialState
  } as Record<BundleTypeKeys, BundleSearchState>,
  reducers: {
    setBundleSearchResults: (
      state,
      action: PayloadAction<{
        key: BundleTypeKeys;
        data: BundleSearchResponse;
      }>
    ) => {
      const { key, data } = action.payload;
      state[key].bundleSearchResults = data;
    },
    setBundleSearchText: (state, action: PayloadAction<{ key: BundleTypeKeys; data: string | null }>) => {
      const { key, data } = action.payload;
      state[key].bundleSearchText = data;
    },
    setBundleSearchHighlightPageId: (state, action: PayloadAction<{ key: BundleTypeKeys; data: string | null }>) => {
      const { key, data } = action.payload;
      state[key].bundleSearchHighlightPageId = data;
    },
    setBundleSearchFocusedPosition: (state, action: PayloadAction<{ key: BundleTypeKeys; data: number | null }>) => {
      const { key, data } = action.payload;
      state[key].bundleSearchFocusedPosition = data;
    },
    resetBundleSearch: () => ({
      [BundleType.LIGHT]: initialState,
      [BundleType.FULL]: initialState
    })
  }
});

export const {
  setBundleSearchResults,
  setBundleSearchText,
  setBundleSearchHighlightPageId,
  setBundleSearchFocusedPosition,
  resetBundleSearch
} = bundleSearchSlice.actions;

export const selectBundleSearchResults = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType ? state.bundleSearch[activeBundleType].bundleSearchResults : initialState.bundleSearchResults;
};

export const selectBundleSearchText = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType ? state.bundleSearch[activeBundleType].bundleSearchText : initialState.bundleSearchText;
};

export const selectBundleSearchHighlightPageId = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleSearch[activeBundleType].bundleSearchHighlightPageId
    : initialState.bundleSearchHighlightPageId;
};

export const selectBundleSearchFocusedPosition = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleSearch[activeBundleType].bundleSearchFocusedPosition
    : initialState.bundleSearchFocusedPosition;
};

export const bundleSearchReducer = bundleSearchSlice.reducer;
